import { useEffect, useState } from 'react'
import { Repeat } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { FormActions, FormField, FormSection } from '@/components/common/FormSection'
import { financeV2Service } from '@/services/financeV2Service'
import { clientService } from '@/services/client-service'
import { financeSummaryService, type CreateRecurringTemplateDto, type RecurringTemplate } from '@/services/financeSummaryService'
import type { Account } from '@/types/financeV2.types'
import type { Client } from '@/types/client'
import { clientDisplayName } from './TransactionFiltersPanel'
import { getErrorMessage } from '@/utils/error.utils'

/** Valida o formulário e monta o DTO (puro, testável). */
export function buildRecurringDto(form: {
  description: string; type: 'INCOME' | 'EXPENSE'; accountId: string; clientId: string
  mode: 'parcela' | 'total'; amount: string; installments: string; firstDueDate: string
}): CreateRecurringTemplateDto | string {
  const amount = Number(String(form.amount).replace(',', '.'))
  const installments = form.installments ? Number(form.installments) : undefined
  if (form.description.trim().length < 3) return 'Descrição muito curta'
  if (!form.accountId) return 'Escolha a conta'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.firstDueDate)) return 'Informe o 1º vencimento'
  if (!(amount > 0)) return 'Valor inválido'
  if (installments !== undefined && (!Number.isInteger(installments) || installments < 1 || installments > 360)) return 'Parcelas: de 1 a 360'
  if (form.mode === 'total' && !installments) return 'Valor total exige o número de parcelas'
  return {
    description: form.description.trim(),
    type: form.type,
    accountId: form.accountId,
    clientId: form.clientId || undefined,
    firstDueDate: form.firstDueDate,
    installments,
    ...(form.mode === 'total' ? { totalAmount: amount } : { installmentAmount: amount }),
  }
}

export function RecurringTemplateForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (t: RecurringTemplate) => void }) {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    description: '', type: 'INCOME' as 'INCOME' | 'EXPENSE', accountId: '', clientId: '',
    mode: 'parcela' as 'parcela' | 'total', amount: '', installments: '', firstDueDate: new Date().toLocaleDateString('en-CA'),
  })
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  useEffect(() => {
    financeV2Service.accounts.list({ isActive: true, limit: 100 }).then((r) => {
      setAccounts(r.data.accounts || [])
      if (r.data.accounts?.[0]) setForm((f) => (f.accountId ? f : { ...f, accountId: r.data.accounts[0].id }))
    }).catch(() => setAccounts([]))
    clientService.getAllClients({ status: 'ACTIVE' }, { page: 1, limit: 1000, sort: 'createdAt:desc' })
      .then((r) => setClients(r.clients || [])).catch(() => setClients([]))
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const dto = buildRecurringDto(form)
    if (typeof dto === 'string') { setError(dto); return }
    try {
      setSaving(true)
      setError(null)
      onCreated(await financeSummaryService.recurring.create(dto))
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao criar o modelo'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="min-w-0">
      <FormSection title="Novo modelo recorrente" icon={Repeat} className="sm:grid-cols-2 lg:grid-cols-4">
        {error && (
          <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive sm:col-span-2 lg:col-span-4">{error}</p>
        )}
        <FormField label="Descrição" htmlFor="recurring-description" className="sm:col-span-2">
          <Input id="recurring-description" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Ex.: Honorários mensais — Cliente X" />
        </FormField>
        <FormField label="Tipo" htmlFor="recurring-type">
          <NativeSelect id="recurring-type" value={form.type} onChange={(e) => set({ type: e.target.value as 'INCOME' | 'EXPENSE' })}>
            <option value="INCOME">A receber</option>
            <option value="EXPENSE">A pagar</option>
          </NativeSelect>
        </FormField>
        <FormField label="Conta" htmlFor="recurring-account">
          <NativeSelect id="recurring-account" value={form.accountId} onChange={(e) => set({ accountId: e.target.value })}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Cliente (opcional)" htmlFor="recurring-client" className="sm:col-span-2">
          <NativeSelect id="recurring-client" value={form.clientId} onChange={(e) => set({ clientId: e.target.value })}>
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{clientDisplayName(c)}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Valor informado é" htmlFor="recurring-mode">
          <NativeSelect id="recurring-mode" value={form.mode} onChange={(e) => set({ mode: e.target.value as 'parcela' | 'total' })}>
            <option value="parcela">por parcela</option>
            <option value="total">total (rateado)</option>
          </NativeSelect>
        </FormField>
        <FormField label="Valor (R$)" htmlFor="recurring-amount">
          <Input id="recurring-amount" inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} />
        </FormField>
        <FormField
          label={<>Parcelas {form.mode === 'parcela' && <span className="font-normal text-muted-foreground">(vazio = sem fim)</span>}</>}
          htmlFor="recurring-installments"
        >
          <Input id="recurring-installments" type="number" min={1} max={360} value={form.installments} onChange={(e) => set({ installments: e.target.value })} />
        </FormField>
        <FormField label="1º vencimento" htmlFor="recurring-first-due">
          <Input id="recurring-first-due" type="date" value={form.firstDueDate} onChange={(e) => set({ firstDueDate: e.target.value })} />
        </FormField>
        <div className="sm:col-span-2 lg:col-span-4">
          <FormActions>
            <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Salvando…' : 'Criar modelo'}</Button>
          </FormActions>
        </div>
      </FormSection>
    </form>
  )
}
